/**
 * Copyright: ThoughtSpot Inc.
 * Author: Vinay kumar nookala (vinay.nookala@thoughtspot.com)
 *
 * @fileoverview
 * This file imports various navigation panels from different MFEs
 * and passes them to the Navigation Hub MFE to render the left navigation under the module tabs.
 */

import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import React, { Suspense, useMemo } from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { useMatch } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { NavigationHub } from '../../../microfrontends/navigation';
import { hasDataTabAccessPrivileges } from '../../../routing/privilege-list';
import { getAppModule } from '../../../routing/routes-util';
import {
    hideHomepageLeftNav,
    isApplicationSwitcherHidden,
    isEverywhereEnabled,
} from '../../../services/system/config-service/session-service';
import { goToApplicationHome } from './left-navigation.util';
import styles from './left-navigation-hub.module.scss';

const NavPanelDataModelContainer = lazy(() =>
    import('../../data-management/nav-panel/nav-panel-data-model.container'),
);

const InsightsNavPanelContainer = lazy(() =>
    import('../../insights/insights-nav-panel.container'),
);

const DevelopLeftNavContainer = lazy(() =>
    import('../../embed-dev-portal/develop-left-nav.container'),
);

const AdminNavPanelContainer = lazy(() =>
    import('../../admin/nav-panel/nav-panel-admin.container'),
);

export interface LeftNavigationHubProps {
    renderFromOverlay?: boolean;
    themeType: string;
    isPrimaryNavHidden?: boolean;
}

export const LeftNavigationHub: React.FC<LeftNavigationHubProps> = ({
    renderFromOverlay,
    themeType,
    isPrimaryNavHidden = false,
}) => {
    const { navService, sessionService } = useAppContext();

    const getInsightsLeftPanel = () => {
        return (
            <Suspense
                fallback={
                    <LoadingIndicator.Global
                        className={styles.loadingIndicator}
                    >
                        <></>
                    </LoadingIndicator.Global>
                }
            >
                <InsightsNavPanelContainer />
            </Suspense>
        );
    };

    const getDataLeftPanel = () => {
        return (
            <Suspense
                fallback={
                    <LoadingIndicator.Global
                        className={styles.loadingIndicator}
                    >
                        <></>
                    </LoadingIndicator.Global>
                }
            >
                <NavPanelDataModelContainer rootPath="/data" />
            </Suspense>
        );
    };

    const getDevelopLeftPanel = () => {
        return (
            <Suspense
                fallback={
                    <LoadingIndicator.Global
                        className={styles.loadingIndicator}
                    >
                        <></>
                    </LoadingIndicator.Global>
                }
            >
                <DevelopLeftNavContainer />
            </Suspense>
        );
    };
    // Use React Router's useMatch for proper route detection
    // Matches /develop and any sub-routes like /develop/playground
    const isDevelopRoute = !!useMatch('/develop/*');
    const hasDeveloperAccess = useMemo(
        () => isDevelopRoute || isEverywhereEnabled(),
        [isDevelopRoute],
    );
    const getAdminNavPanel = () => {
        return (
            <Suspense
                fallback={
                    <LoadingIndicator.Global
                        className={styles.loadingIndicator}
                    >
                        <></>
                    </LoadingIndicator.Global>
                }
            >
                <AdminNavPanelContainer />
            </Suspense>
        );
    };

    return (
        <NavigationHub
            insightsLeftPanelFn={getInsightsLeftPanel}
            dataLeftPanelFn={getDataLeftPanel}
            developLeftPanelFn={getDevelopLeftPanel}
            adminNavPanelFn={getAdminNavPanel}
            currentAppModule={getAppModule()}
            goToApplicationHome={(
                applicationID: string,
                event: React.MouseEvent,
            ) => goToApplicationHome(applicationID, navService, event)}
            canShowInsightsTab={!hideHomepageLeftNav(true)}
            canAccessDataTab={hasDataTabAccessPrivileges()}
            hasDeveloperAccess={isEverywhereEnabled()}
            hasAdminAccess={
                (sessionService.isAdminTabVisible() ||
                    sessionService.enableTeamsAdminTab()) &&
                !sessionService.isApplicationSwitcherHidden()
            }
            hideTabSwitcher={
                // When the primary nav is hidden, the tab switcher should be hidden.
                isApplicationSwitcherHidden() || isPrimaryNavHidden
            }
            renderFromOverlay={renderFromOverlay}
            themeType={themeType}
        />
    );
};
